import { CUSTOM_ELEMENTS_SCHEMA, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { IconDirective, SharedScrollEndNotifierDirective, SpinnerComponent } from '@seech/shared-ng';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';

export interface Currency {
  currencyCode: string;
  currencyName: string;
  countryCode: string;
  currencySymbol?: string;
}

@Component({
  selector: 'seech-currency',
  templateUrl: './currency.component.html',
  styleUrls: ['./currency.component.scss'],
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CurrencyComponent),
      multi: true,
    }
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    IconDirective,
    SharedScrollEndNotifierDirective,
    FormsModule,
    CommonModule,
    SpinnerComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,

})
export class CurrencyComponent implements ControlValueAccessor, Validator, OnChanges {
  @Input() selectedValue: any = {}
  @Input() options: Currency[] = [];
  @Input() label?: string;
  @Input() id = '';
  @Input() isContentLoading = false;
  @Input() autocomplete: 'on' | 'off' = 'off';
  @Input() threshold = 100;
  @Input() variant: 'single-line' | 'multi-line' = 'single-line';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() showSearch = true;
  @Input() debounceTime = 1000;
  @Input() defaultCurrencyCode = 'USD';

  @Output() closed = new EventEmitter<boolean>();
  @Output() opened = new EventEmitter<boolean>();
  @Output() selected: any = new EventEmitter<Currency>();
  @Output() scrollEnd = new EventEmitter<void>();
  @Output() searchValueChange = new EventEmitter<string>();
  @Output() defaultCurrencyRequested = new EventEmitter<string>();

  showDropdown = false;
  private searchSubject = new Subject<string>();

  @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  ngOnChanges(changes: SimpleChanges): void {
    if ('selectedValue' in changes) {
      this.selectOption(this.selectedValue, 0);
    }

    if ('options' in changes) {
      this.options = changes['options'].currentValue;

      // Set default currency if no value selected and options just loaded
      if (!this.selectedValue?.currencyCode && this.defaultCurrencyCode && this.options.length > 0) {
        this.setDefaultCurrency();
      }
    }
  }

  constructor(
    private eRef: ElementRef,
    private cdr: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.searchSubject
      .pipe(
        debounceTime(this.debounceTime),
        distinctUntilChanged()
      )
      .subscribe(value => {
        this.searchValueChange.emit(value);
        this.onChange(value);
        this.onTouched();
      });

    // Set default currency if no value is set
    if (!this.selectedValue?.currencyCode && this.defaultCurrencyCode) {
      this.setDefaultCurrency();
    }
  }

  handleViewDropDown(show: boolean) {
    if (this.readonly || this.disabled) return;
    this.showDropdown = show;
    show === true ? this.opened.emit(true) : this.opened.emit(false);

    if (show) {
      this.focusSearchInput();
    }

    this.cdr.detectChanges();
  }

  selectOption(option: any, index: number) {
    this.selectedValue = option;
    this.onChange(option);
    this.onTouched();
    this.selected.emit(option);
    this.handleViewDropDown(false);
    this.cdr.detectChanges();
  }

  onInput(event: any) {
    this.showDropdown = true;
    this.searchSubject.next(event);
  }

  private focusSearchInput() {
    requestAnimationFrame(() => {
      const el = this.searchInput?.nativeElement;
      if (el) {
        el.focus();
        el.select();
      }
    });
  }

  private setDefaultCurrency(): void {
    if (!this.defaultCurrencyCode) {
      return;
    }

    const defaultCurrency = this.options.find(
      c => c.currencyCode === this.defaultCurrencyCode
    );

    if (defaultCurrency) {
      // Currency is in list, set it
      this.selectedValue = defaultCurrency;
      this.onChange(defaultCurrency);
    } else {
      // Currency not in list, emit event
      this.defaultCurrencyRequested.emit(this.defaultCurrencyCode);
    }
    this.cdr.markForCheck();
  }


  private onChange = (value: any) => { };

  private onTouched = () => { };

  writeValue(value: any): void {
    if (value) {
      this.selectedValue = value;
    }
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const valueIsRequired = control.hasValidator(Validators.required);
    const requiredError = { required: true };
    if (!value && valueIsRequired) {
      return requiredError;
    }
    return null;
  }

  @HostListener('document:click', ['$event.target'])
  public onClick(targetElement) {
    const clickedInside = this.eRef.nativeElement.contains(targetElement);
    if (!clickedInside && this.showDropdown) {
      this.handleViewDropDown(false);
    }
  }

  onScrollEnd() {
    this.scrollEnd.emit();
  }

}
